/**
 * Phase 45.1 — Feature flag client.
 *
 * Goal: every risky change ships behind a flag. Beta-cohort coaches get
 * the new behavior 24-72h before everyone else, so we catch regressions
 * BEFORE they hit the full user base.
 *
 * Backend contract: GET /api/feature-flags returns
 *   { flags: { feature_a: true, feature_b: false, ... }, rollout_group: "beta"|"stable" }
 * The flag value is ALREADY EVALUATED for the current user's cohort, so
 * the frontend just consumes booleans. Keeps client logic dumb and the
 * trust boundary at the backend.
 *
 * Usage:
 *   import { useFeatureFlag } from '@/lib/featureFlags';
 *   const showNewBilling = useFeatureFlag('new_billing_ui');
 *   return showNewBilling ? <NewBilling/> : <OldBilling/>;
 *
 * Caching:
 *   - One fetch on app boot (FeatureFlagProvider mounts)
 *   - 60s revalidation on focus (so toggling a flag in admin shows up
 *     within a minute even for already-loaded tabs)
 *   - Optimistic default: every flag is `false` until first fetch
 *     resolves — safe because "show old behavior" is always the
 *     correct conservative default
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const FeatureFlagCtx = createContext({ flags: {}, rolloutGroup: 'stable', loading: true, reload: () => { } });

export function FeatureFlagProvider({ children }) {
    const [flags, setFlags] = useState({});
    const [rolloutGroup, setRolloutGroup] = useState('stable');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/feature-flags');
            setFlags(r.data?.flags || {});
            setRolloutGroup(r.data?.rollout_group || 'stable');
        } catch {
            // Never block app boot on a failed flag fetch — degrade to
            // "everything off (= old behavior)" which is the safe path.
            setFlags({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Refetch when the tab regains focus so a flag flipped in admin
    // propagates to live users within ~60s.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [load]);

    return (
        <FeatureFlagCtx.Provider value={{ flags, rolloutGroup, loading, reload: load }}>
            {children}
        </FeatureFlagCtx.Provider>
    );
}

/**
 * Returns `true` if the named flag is enabled for the current user.
 * Falls back to `false` while the initial fetch is in-flight — that's
 * intentional: render the OLD path until we know for sure that the
 * NEW path is on. Prevents UI flashes for users not in the cohort.
 */
export function useFeatureFlag(name) {
    const { flags } = useContext(FeatureFlagCtx);
    return !!flags[name];
}

/** Returns the full flag map. Useful for debug panels. */
export function useAllFeatureFlags() {
    return useContext(FeatureFlagCtx);
}
